import * as React from "react";
import { useState, useEffect } from "react";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { Card, CardContent, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import apiService from "../../../../../../service/Api";

const LIGHT_BLUE = "#6fabddcc";
const DARK_BLUE = "#4a96e2ff";

// ---------------- Center Label ----------------
const StyledText = styled("text")(() => ({
  fill: "#ffffff",
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 18,
  fontWeight: 600,
}));

function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}

// ---------------- Center Circle ----------------
function PieCenterCircle({ radius }: { radius: number }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <circle
      cx={left + width / 2}
      cy={top + height / 2}
      r={radius}
      fill={DARK_BLUE}
    />
  );
}

// ---------------- Widget ----------------
interface Suburb {
  suburb: string;
  venue_count: number;
}

interface CitySuburbItem {
  city: string;
  suburbs: Suburb[];
  total_venues?: number; // optional
}

interface CitySuburbResponse {
  data: CitySuburbItem[];
  total_cities: number;
  title: string;
}

const CitySuburbWidget = () => {
  const [city, setCity] = useState<string>("City");
  const [suburbsData, setSuburbsData] = useState<
    { id: string; value: number; label: string; color?: string }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const result = await apiService.get<CitySuburbResponse>(
          "/api/venues/dashboard/city-suburb-hierarchy/"
        );

        const items = result.data?.data ?? [];
        if (!items.length) return;

        const firstCity = items[0];
        setCity(firstCity.city);

        const suburbs = firstCity.suburbs
          .filter((s) => s.venue_count > 0)
          .map((s) => ({
            id: s.suburb || "Unknown",
            value: s.venue_count,
            label: s.suburb || "Unknown",
            color: LIGHT_BLUE,
          }));

        setSuburbsData(suburbs);
      } catch (error) {
        console.error("Error fetching city-suburb data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) return <Typography>Loading...</Typography>;
  if (!suburbsData.length) return <Typography>No data available</Typography>;

  return (
    <Card sx={{ height: "100%", width: "100%", borderRadius: 3 }}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Typography variant="h6" textAlign="center" sx={{ mb: 2 }}>
          City–Suburb Hierarchy
        </Typography>

        <Box
          sx={{
            width: "100%", // full width
            maxWidth: 900, // optional: limit max width
            height: 500, // bigger height to avoid squashing
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <PieChart
            width={900}
            height={500}
            series={[
              {
                data: suburbsData,
                innerRadius: 100, // larger inner radius
                outerRadius: 200, // larger outer radius
                paddingAngle: 0.5,
                arcLabel: (item) => `${item.label} (${item.value})`,
                highlightScope: { fade: "global", highlight: "item" },
                faded: { innerRadius: 100, additionalRadius: -10 },
              },
            ]}
            sx={{
              [`& .${pieArcLabelClasses.root}`]: {
                fontSize: 12,
                fill: "#ffffff",
              },
            }}
            hideLegend
          >
            <PieCenterCircle radius={90} />
            <PieCenterLabel>{city}</PieCenterLabel>
          </PieChart>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CitySuburbWidget;
